import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import ldStartCase from 'lodash/startCase';
import {
  formatState,
  formatPhoneNumber,
  formatGender,
  formatEthnicity,
} from '@purple/match-graphql-schema/lib/activeMatchExportUtils';
import Table from '../../components/Table';
import messages from './messages';
import './styles.css';

const separateNonNullDataWithCommas = (...args) => {
  const nonNullData = [];
  args.forEach((arg) => {
    if (arg) nonNullData.push(arg);
  });
  return nonNullData.join(', ');
};

const formatGenderAndEthnicity = (cnxion) =>
  separateNonNullDataWithCommas(formatGender(cnxion.gender), formatEthnicity(cnxion.ethnicity));

const Cell = ({ when, children, placeholder = null, ...rest }) => {
  if (when === false && !placeholder) return null;
  return <Table.Cell {...rest}>{when !== false ? children : placeholder}</Table.Cell>;
};
Cell.propTypes = {
  when: PropTypes.bool,
  children: PropTypes.node,
  placeholder: PropTypes.node,
};

const startCase = (str) => {
  if (!str) return '';
  return ldStartCase(str.toLowerCase());
};

const AMConnectionRow = ({ connection, intl, extendedLayout, removeDiversityFilters }) => {
  const na = intl.formatMessage(messages.notApplicable);
  const naviance = connection.source !== 'CAMINO';
  const camino = connection.source === 'CAMINO';
  return (
    <Table.Row>
      <Table.Cell>
        <span styleName="bold">
          {startCase(connection.firstName)} {startCase(connection.lastName)}
        </span>
      </Table.Cell>
      {extendedLayout && <Table.Cell>{naviance ? 'Naviance' : 'Camino'}</Table.Cell>}
      {!removeDiversityFilters && (
        <Cell when={naviance} placeholder={na}>
          <span><span styleName="gender-ethnicity-label">{formatGenderAndEthnicity(connection)}</span><br /></span>
        </Cell>
      )}
      <Table.Cell>
        {connection.birthDate && (
          <FormattedMessage
            {...messages.bornOn}
            values={{
              date: intl.formatDate(connection.birthDate, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }),
            }}
          />
        )}
      </Table.Cell>
      <Table.Cell>
        {connection.graduationYear && (
          <span>
            <span styleName="bold">{connection.gradeYearGPA}</span>
            <FormattedMessage
              {...messages.classOf}
              values={{ year: connection.graduationYear }}
            />
            <br />
          </span>
        )}
        {connection.gpa &&
        <span><span styleName="bold">{connection.gpa}</span> <FormattedMessage {...messages.gpa} /><br /></span>}
      </Table.Cell>
      <Cell when={naviance} placeholder={na}>
        {connection.schoolName}
        <br />
        <FormattedMessage {...messages.ceeb} values={{ value: connection.ceeb }} />
      </Cell>
      <Table.Cell>
        {formatPhoneNumber(connection.phone || '')}
        <br />
        <span>{(connection.email || '').toLowerCase()} </span>
        {naviance && (
          <React.Fragment>
            <br />
            <br />
            <span>{startCase(connection.address)} </span>
            <br />
            <span>{
              separateNonNullDataWithCommas(
                startCase(connection.city),
                formatState(connection.state),
                connection.zip,
              )
            }
            </span>
          </React.Fragment>
        )}
      </Table.Cell>
      <Table.Cell>
        {connection.majors ? connection.majors.map((m) => m.name).join(', ') : ''}
      </Table.Cell>
      {extendedLayout && (
        <Cell when={camino} placeholder={na}>
          {connection.schoolName}
          <br />
          {connection.creditsEarned}
        </Cell>
      )}
      {extendedLayout && (
        <Cell when={camino} placeholder={na}>
          {connection.transferBy}
        </Cell>
      )}

      <Table.Cell>
        <FormattedMessage
          {...messages[connection.disconnectedStatus === 0 ? 'addedOn' : 'disconnectedOn']}
          values={{
            date: intl.formatDate((connection.dateUpdated ? connection.dateUpdated : connection.dateSubmitted), {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            }),
          }}
        />
      </Table.Cell>
    </Table.Row>
  );
};

AMConnectionRow.propTypes = {
  connection: PropTypes.object.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }),
  extendedLayout: PropTypes.bool,
  removeDiversityFilters: PropTypes.bool,
};

export default injectIntl(AMConnectionRow);
